import { useQuery } from '@tanstack/react-query'
import { Building2 } from 'lucide-react'
import { api } from '@/lib/api-client'
import { formatDateTime } from '@/lib/utils'
import { PageHeader } from '@/components/ui/page-header'
import { Card } from '@/components/ui/card'
import { StatusBadge } from '@/components/ui/badge'
import { EmptyState } from '@/components/ui/empty-state'
import { FilterBar, type FilterDef } from '@/components/data/filter-bar'
import { DataTable } from '@/components/data/data-table'
import { useState } from 'react'

interface GlobalM365Tenant {
  id: string
  azure_tenant_id: string
  display_name: string
  domain: string | null
  status: string
  last_sync_at: string | null
  last_sync_status: string | null
  sync_enabled: number
  created_at: string
  client_id: string
  client_name: string
}

const filters: FilterDef[] = [
  { key: 'search', label: 'Buscar', type: 'search', placeholder: 'Buscar por nombre, dominio o cliente...' },
]

export function AdminM365TenantsPage() {
  const [filterValues, setFilterValues] = useState<Record<string, string>>({})

  const { data: tenants, isLoading } = useQuery({
    queryKey: ['admin', 'm365-tenants', filterValues.search],
    queryFn: () => {
      const params = filterValues.search ? `?search=${encodeURIComponent(filterValues.search)}` : ''
      return api.get<GlobalM365Tenant[]>(`/admin/m365-tenants${params}`)
    },
  })


  return (
    <div className="space-y-6">
      <PageHeader title="Tenants Microsoft 365" description="Todos los tenants M365 conectados en la plataforma" />

      <FilterBar
        filters={filters}
        values={filterValues}
        onChange={(k, v) => setFilterValues((prev) => ({ ...prev, [k]: v }))}
        onClear={() => setFilterValues({})}
      />

      <Card padding={false}>
        {isLoading ? (
          <div className="p-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="mb-3 h-12 animate-pulse rounded" style={{ backgroundColor: 'var(--color-gray-100)' }} />
            ))}
          </div>
        ) : !tenants?.length ? (
          <div className="p-6">
            <EmptyState icon={Building2} title="Sin tenants M365" description="Los clientes aun no han conectado ningun tenant" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <DataTable
              columns={[
                {
                  key: 'display_name', header: 'Tenant',
                  render: (r) => {
                    const row = r as unknown as GlobalM365Tenant
                    return (
                      <div>
                        <p className="font-medium" style={{ color: 'var(--color-text-primary)' }}>{row.display_name}</p>
                        <p className="text-xs" style={{ color: 'var(--color-text-tertiary)' }}>{row.domain ?? row.azure_tenant_id}</p>
                      </div>
                    )
                  },
                },
                {
                  key: 'client_name', header: 'Cliente',
                  render: (r) => {
                    const row = r as unknown as GlobalM365Tenant
                    return (
                      <a
                        href={`/admin/clients/${row.client_id}`}
                        className="text-sm font-medium hover:underline"
                        style={{ color: 'var(--color-brand-secondary)' }}
                      >
                        {row.client_name}
                      </a>
                    )
                  },
                },
                {
                  key: 'status', header: 'Estado',
                  render: (r) => <StatusBadge status={(r as unknown as GlobalM365Tenant).status} />,
                },
                {
                  key: 'sync_enabled', header: 'Sync',
                  render: (r) => {
                    const row = r as unknown as GlobalM365Tenant
                    return (
                      <span className="text-xs font-medium" style={{ color: row.sync_enabled ? 'var(--color-success)' : 'var(--color-text-tertiary)' }}>
                        {row.sync_enabled ? 'Activa' : 'Desactivada'}
                      </span>
                    )
                  },
                },
                {
                  key: 'last_sync_at', header: 'Ultima sync',
                  render: (r) => {
                    const row = r as unknown as GlobalM365Tenant
                    return (
                      <div className="flex items-center gap-2">
                        <span className="text-sm tabular-nums">{formatDateTime(row.last_sync_at)}</span>
                        {row.last_sync_status && <StatusBadge status={row.last_sync_status} />}
                      </div>
                    )
                  },
                },
                {
                  key: 'actions', header: '',
                  render: (r) => {
                    const row = r as unknown as GlobalM365Tenant
                    return (
                      <a
                        href={`/admin/clients/${row.client_id}`}
                        className="inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-xs font-medium transition-colors hover:bg-gray-100"
                        style={{ color: 'var(--color-brand-secondary)' }}
                      >
                        Gestionar
                      </a>
                    )
                  },
                },
              ]}
              data={(tenants ?? []) as unknown as Record<string, unknown>[]}
            />
          </div>
        )}
      </Card>
    </div>
  )
}
